import { useMemo, type ReactNode } from 'react'
import { Check, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TD, TH } from '@/features/fraud/format'
import type { DiscountPreviewResponse } from '@/lib/api/discount-types'
import { formatInEventZone, timeZoneLabel } from '@/lib/eventTime'
import { cn } from '@/lib/utils'
import { Section } from './editor-fields'
import { HINT } from './editor-styles'
import { formatMenuPrice, type MenuUnits } from './money'

/** The last preview asked for: `key` is the draft body it priced. */
export interface PreviewState {
  key: string
  loading: boolean
  data: DiscountPreviewResponse | null
  error: string | null
}

/** Rows rendered at once; a whole vendor can target hundreds of items. */
const MAX_ROWS = 200

const NUM_TD = cn(TD, 'tabular-nums')

export function PreviewPanel({ state, current, autoPreview, onRefresh, units, timeZone, describeItem, outsideEvent, note }: {
  state: PreviewState | null
  /** `state` priced the draft as it stands now. */
  current: boolean
  /** The draft is complete, so the preview refreshes on its own. */
  autoPreview: boolean
  onRefresh: () => void
  units: MenuUnits
  timeZone: string
  describeItem: (itemId: number) => { name: string; location: string | null }
  /** The draft has no window inside the dates the event's prices cover, so it is never charged. */
  outsideEvent?: boolean
  /** Shown at the top of the section, e.g. that a paused draft is priced as if running. */
  note?: ReactNode
}) {
  const data = state?.data ?? null
  const rows = useMemo(() => {
    if (!data) return []
    return data.items
      .map((row) => ({ row, ...describeItem(row.id) }))
      .sort((a, b) => (a.location ?? '').localeCompare(b.location ?? '') || a.name.localeCompare(b.name))
  }, [data, describeItem])
  const winning = rows.filter(({ row }) => row.wins).length
  const free = rows.filter(({ row }) => row.free).length
  const loading = !!state?.loading
  // Judged from the draft as it stands, so only said of a preview of that draft.
  const datesExplain = current && !!outsideEvent && winning === 0

  let status: string
  if (loading) status = data ? 'Updating…' : 'Pricing the draft…'
  else if (state?.error && current) status = ''
  else if (!state) status = autoPreview ? 'Pricing the draft…' : 'Finish the rule, targets and window to see prices, or press Preview prices.'
  else if (!current) status = autoPreview ? 'The draft changed. Updating…' : 'The draft changed since this preview. Press Preview prices to update it.'
  else status = ''

  return (
    <Section
      title="Preview"
      description="What each targeted item would be charged, with the event's other schedules and item sales taken into account."
      aside={
        <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          Preview prices
        </Button>
      }
    >
      {note}
      {status && <p className={HINT}>{status}</p>}
      {state?.error && current && !loading && (
        <p className="rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300">
          No preview yet: {state.error}
        </p>
      )}
      {data && (
        <div className={cn('space-y-2', !current && 'opacity-60')}>
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Prices as they would be at{' '}
            <span className="font-medium">{formatInEventZone(data.at, timeZone, "EEE, MMM d, yyyy 'at' h:mm a")}</span>{' '}
            ({timeZoneLabel(timeZone)}).
          </p>
          {rows.length === 0 ? (
            <p className={HINT}>The draft targets no items yet.</p>
          ) : (
            <>
              <p className={HINT}>
                {rows.length} {rows.length === 1 ? 'item' : 'items'} · {winning} charged at this schedule's price
                {free > 0 && <> · <span className="font-semibold text-amber-700 dark:text-amber-300">{free} free</span></>}
              </p>
              {datesExplain && (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Nothing is charged at this schedule's price: its window is outside the event's dates.
                </p>
              )}
              <div className="max-h-96 overflow-auto rounded-md border border-neutral-200 dark:border-neutral-800">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-neutral-50 dark:bg-neutral-900">
                    <tr>
                      <th className={TH}>Item</th>
                      <th className={TH}>List</th>
                      <th className={TH}>Sale</th>
                      <th className={TH}>This schedule</th>
                      <th className={TH}>Charged</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                    {rows.slice(0, MAX_ROWS).map(({ row, name, location }) => (
                      <tr key={row.id} className={cn(row.free && 'bg-amber-50 dark:bg-amber-900/20')}>
                        <td className={TD}>
                          <div className="font-medium text-neutral-900 dark:text-white">{name}</div>
                          {location && <div className="text-xs text-neutral-500 dark:text-neutral-400">{location}</div>}
                        </td>
                        <td className={cn(NUM_TD, 'text-neutral-600 dark:text-neutral-400')}>{formatMenuPrice(row.list_price, units)}</td>
                        <td className={cn(NUM_TD, 'text-neutral-600 dark:text-neutral-400')}>
                          {row.sale_price == null ? '—' : formatMenuPrice(row.sale_price, units)}
                        </td>
                        <td className={NUM_TD}>
                          {row.excluded ? <span className="text-neutral-500">Excluded</span> : formatMenuPrice(row.schedule_price, units)}
                        </td>
                        <td className={NUM_TD}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-semibold text-neutral-900 dark:text-white">{formatMenuPrice(row.effective_price, units)}</span>
                            {row.free && (
                              <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-amber-900 dark:bg-amber-500/30 dark:text-amber-100">
                                Free
                              </span>
                            )}
                            {row.wins && (
                              <span className="inline-flex items-center gap-0.5 rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                <Check className="h-3 w-3" /> This schedule
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > MAX_ROWS && <p className={HINT}>And {rows.length - MAX_ROWS} more items.</p>}
            </>
          )}
        </div>
      )}
    </Section>
  )
}
